// =============================================================================
// AGC: [AUDIT] Audit Log API Service
// AGC: Frontend service for lot audit log operations
// AGC: Handles fetching audit logs and exporting in various formats
// =============================================================================

import { getBackendUrl } from '../utils/backendUrl'
import type { LotAuditLog, AuditExportFormat, AuditExportResponse } from '../types'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

/**
 * AGC: [AUDIT] Build headers for API requests
 */
const buildHeaders = (authToken?: string | null, userId?: string, fieldId?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }
  
  if (userId) {
    headers['x-user-id'] = userId
  }
  
  if (fieldId) {
    headers['x-field-id'] = fieldId
  }
  
  return headers
}

/**
 * AGC: [AUDIT] Fetch audit log for a specific lot
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID containing the lot
 * @param authToken - Authentication token
 * @param userId - User ID
 * @param options - Additional options { tracesLimit, eventsLimit }
 * @returns Promise<LotAuditLog> - The audit log data
 */
export const getLotAuditLog = async (
  lotId: string,
  fieldId: string,
  authToken?: string | null,
  userId?: string,
  options?: { tracesLimit?: number; eventsLimit?: number }
): Promise<LotAuditLog | null> => {
  try {
    const headers = buildHeaders(authToken, userId, fieldId)
    
    const queryParams = new URLSearchParams({
      fieldId,
    })
    
    if (options?.tracesLimit) {
      queryParams.set('tracesLimit', options.tracesLimit.toString())
    }
    
    if (options?.eventsLimit) {
      queryParams.set('eventsLimit', options.eventsLimit.toString())
    }
    
    const response = await fetch(
      `${BASE_URL}/api/v1/lots/${lotId}/audit-log?${queryParams.toString()}`,
      {
        method: 'GET',
        headers,
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      if (response.status === 404) {
        console.warn(`⚠️ AGC: [AUDIT] No audit log found for lot ${lotId}`)
        return null
      }
      throw new Error(`Failed to fetch audit log: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data as LotAuditLog
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error fetching audit log:', error)
    return null
  }
}

/**
 * AGC: [AUDIT] Export audit log in specified format
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID containing the lot
 * @param format - Export format ('json', 'xml', 'pdf')
 * @param authToken - Authentication token
 * @param userId - User ID
 * @returns Promise<Blob | null> - The exported file as Blob
 */
export const exportLotAuditLog = async (
  lotId: string,
  fieldId: string,
  format: AuditExportFormat = 'json',
  authToken?: string | null,
  userId?: string
): Promise<Blob | null> => {
  try {
    const headers = buildHeaders(authToken, userId, fieldId)
    
    const queryParams = new URLSearchParams({
      fieldId,
      format,
    })
    
    const response = await fetch(
      `${BASE_URL}/api/v1/lots/${lotId}/audit-log/export?${queryParams.toString()}`,
      {
        method: 'GET',
        headers,
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to export audit log: ${response.statusText}`)
    }

    // AGC: Return as blob for file download
    return await response.blob()
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error exporting audit log:', error)
    return null
  }
}

/**
 * AGC: [AUDIT] Download audit log export as file
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID containing the lot
 * @param format - Export format ('json', 'xml', 'pdf')
 * @param lotName - Lot name for filename
 * @param authToken - Authentication token
 * @param userId - User ID
 */
export const downloadAuditLog = async (
  lotId: string,
  fieldId: string,
  format: AuditExportFormat = 'json',
  lotName: string,
  authToken?: string | null,
  userId?: string
): Promise<boolean> => {
  try {
    const blob = await exportLotAuditLog(lotId, fieldId, format, authToken, userId)
    
    if (!blob) {
      console.error('❌ AGC: [AUDIT] No data received for export')
      return false
    }

    // AGC: Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // AGC: Generate filename with lot name and date
    const date = new Date().toISOString().split('T')[0]
    const sanitizedName = lotName.replace(/[^a-zA-Z0-9]/g, '_')
    const extension = format === 'pdf' ? 'html' : format // AGC: PDF currently returns HTML
    link.download = `auditoria_${sanitizedName}_${date}.${extension}`
    
    // AGC: Trigger download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // AGC: Cleanup
    window.URL.revokeObjectURL(url)
    
    console.log(`✅ AGC: [AUDIT] Audit log downloaded: ${link.download}`)
    return true
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error downloading audit log:', error)
    return false
  }
}

/**
 * AGC: [AUDIT] Check if audit log is available for a lot
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID
 * @param authToken - Authentication token
 * @param userId - User ID
 * @returns Promise<boolean> - Whether audit log exists
 */
export const hasAuditLog = async (
  lotId: string,
  fieldId: string,
  authToken?: string | null,
  userId?: string
): Promise<boolean> => {
  try {
    const auditLog = await getLotAuditLog(lotId, fieldId, authToken, userId, {
      tracesLimit: 1,
      eventsLimit: 1,
    })
    
    return auditLog !== null && (auditLog.totalTraces > 0 || auditLog.totalEvents > 0)
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error checking audit log availability:', error)
    return false
  }
}

/**
 * AGC: [AUDIT] Get audit service status
 * @returns Promise<object | null> - Service status info
 */
export const getAuditServiceStatus = async (): Promise<{
  status: string
  service: string
  version: string
  features: Record<string, boolean>
} | null> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/lots/audit/status`)
    
    if (!response.ok) {
      return null
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error checking audit service status:', error)
    return null
  }
}

/**
 * AGC: [AUDIT] Emit weather health computation event
 * AGC: Call this from the frontend when WeatherHealth is calculated
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID containing the lot
 * @param weatherHealthData - The weather health metrics
 * @param authToken - Authentication token
 * @param userId - User ID
 */
export const emitWeatherHealthEvent = async (
  lotId: string,
  fieldId: string,
  weatherHealthData: {
    cropId: string
    daysSincePlanting: number
    plantingDate?: string | Date
    scores: { precipitation: number; temperature: number; radiation: number; overall: number }
    precipitation: { required: number; accumulated: number; deficit: number }
    temperature: { optimalMin: number; optimalMax: number; avgReal: number; nightsAbove24?: number; nightsBelow10?: number }
    radiation: { required: number; accumulated: number; deficit: number }
    lotName?: string
    lotArea?: number
    geolocation?: { lat: number; lng: number }
  },
  authToken?: string | null,
  userId?: string
): Promise<boolean> => {
  try {
    const headers = buildHeaders(authToken, userId, fieldId)
    
    const response = await fetch(
      `${BASE_URL}/api/v1/lots/${lotId}/audit-log/weather-health?fieldId=${fieldId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(weatherHealthData),
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('⚠️ AGC: [AUDIT] Auth required for weather health event')
        return false
      }
      throw new Error(`Failed to emit weather health event: ${response.statusText}`)
    }

    console.log(`✅ AGC: [AUDIT] Weather health event emitted for lot ${lotId}`)
    return true
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error emitting weather health event:', error)
    return false
  }
}

/**
 * AGC: [AUDIT] Emit inputs generated event
 * AGC: Call this from the frontend when task planning is completed
 * @param lotId - The lot/plot ID
 * @param fieldId - The field ID containing the lot
 * @param inputsData - The inputs summary
 * @param authToken - Authentication token
 * @param userId - User ID
 */
export const emitInputsGeneratedEvent = async (
  lotId: string,
  fieldId: string,
  inputsData: {
    planId?: string
    taskCount: number
    inputs: Array<{ genericName: string; quantityPerHa: number; totalQuantity: number; unit: string }>
  },
  authToken?: string | null,
  userId?: string
): Promise<boolean> => {
  try {
    const headers = buildHeaders(authToken, userId, fieldId)
    
    const response = await fetch(
      `${BASE_URL}/api/v1/lots/${lotId}/audit-log/inputs-generated?fieldId=${fieldId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...inputsData,
          totalInputTypes: inputsData.inputs.length,
        }),
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('⚠️ AGC: [AUDIT] Auth required for inputs generated event')
        return false
      }
      throw new Error(`Failed to emit inputs generated event: ${response.statusText}`)
    }

    console.log(`✅ AGC: [AUDIT] Inputs generated event emitted for lot ${lotId}`)
    return true
  } catch (error) {
    console.error('❌ AGC: [AUDIT] Error emitting inputs generated event:', error)
    return false
  }
}

// =============================================================================
// AGC: [AUDIT] DEFAULT EXPORT
// =============================================================================

export default {
  getLotAuditLog,
  exportLotAuditLog,
  downloadAuditLog,
  hasAuditLog,
  getAuditServiceStatus,
  emitWeatherHealthEvent,
  emitInputsGeneratedEvent,
}

